import { AxiosError } from 'axios';
import { logger } from '@shared/logger';
import { refreshAfterError } from '@shared/utils';
import { TIMEOUT_MESSAGE } from '@core/shared/types';
import i18n from 'i18next';
import useChatStore from '@core/stores/useChatStore';
import { authService } from '@core/services/authService';
import queryClient from '@core/services/queryClient';
import useUserStore from '@core/stores/useUserStore';
import dayjs from 'dayjs';

/**
 * Handles errors from API calls by logging and updating the UI error state.
 *
 * This function processes errors from Axios requests or generic errors. In the case of an Axios error,
 * it clears queries from the query client, handles specific HTTP status codes (such as 404 and 401),
 * and logs error details. For non-Axios errors, it logs the error.
 *
 * @param {unknown} error - The error object encountered during the API call.
 * @param {string} storeKey - A unique key used to log and manage errors within the application state.
 * @returns {string} The error message that should be displayed to the user.
 */

export const handleError = async (error: unknown, storeKey: string): Promise<string> => {
    let errorMessage = i18n.t('error.unknown');

    if (error instanceof AxiosError) {
        // Log and clear any stale queries to maintain data consistency
        logger.log(storeKey, 1, 'Clearing queries from queryClient', error);
        await queryClient.invalidateQueries();

        const status = error.response?.status;
        const responseMessage = error.response?.data?.detail?.message;
        const requestUrl = error.config?.url || '';
        const isExternalLangdockChatRequest =
            requestUrl.includes('/agent/v1/chat/completions') ||
            requestUrl.includes('/langdock-proxy/');

        // Handle timeout errors by checking for a specific timeout message
        if (error.toString().includes(TIMEOUT_MESSAGE)) {
            errorMessage = i18n.t('error.timeout');
        }

        // Redirect on 404 errors
        else if (status === 404) {
            window.location.href = '/';
            return errorMessage;
        }

        // Handle unauthorized errors by refreshing token and logging extra details
        else if (status === 401) {
            errorMessage = responseMessage || 'Unauthorized';

            // Direct Langdock chat can fail independently from MSAL auth; avoid forcing global refresh/reload loops.
            if (isExternalLangdockChatRequest) {
                useChatStore.getState().setError(storeKey, errorMessage);
                return errorMessage;
            }

            await authService.refreshToken(true);

            if (refreshAfterError()) {
                return errorMessage;
            }
        }

        // Handle all other Axios errors
        else {
            errorMessage = responseMessage || errorMessage;
        }
    }

    // If the error is a generic JavaScript error, log and capture it accordingly
    else if (error instanceof Error) {
        errorMessage = error.message || errorMessage;
    }

    useChatStore.getState().setError(storeKey, errorMessage);
    return errorMessage;
};
